import React, { FC, useContext, useMemo } from 'react';
import convertStreamableLink from 'utils/streamableLinkUtils';
import ReactPlayer from 'react-player';
import { Stream } from '@cloudflare/stream-react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { selectLocale } from 'models/localization';
import { useSelector } from 'react-redux';
import queryString from 'query-string';
import { detect } from 'detect-browser';
import { UserSessionContext } from 'components/UserSession';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import useEncodedLiveWebinarUrl from 'hooks/useEncodedLiveWebinarUrl';
import { ZOOM_HOSTING_ENDPOINT } from 'appenv';
import { WebinarPropType } from '../../models/types/webinars';

const CloudFlareVideoWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
`;

const EmbeddedVideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 56.25%;
  margin-bottom: 24px;
`;

const EmbeddedZoomWrapper = styled(EmbeddedVideoContainer)`
  height: 560px;
`;

const StyledIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
  background-color: black;
`;

const CloudFareStream = ({ id }) => (
  <EmbeddedVideoContainer id="embedded-video-container">
    <CloudFlareVideoWrapper id="webinar-cloudflare-stream">
      <Stream
        controls
        autoplay
        src={id}
        height="100%"
        width="100%"
      />
    </CloudFlareVideoWrapper>
  </EmbeddedVideoContainer>
);

CloudFareStream.propTypes = {
  id: PropTypes.string.isRequired,
};

const VideoPlayer = ({ url }) => (
  <EmbeddedVideoContainer id="embedded-video-container">
    <ReactPlayer
      id="webinar-react-player"
      url={url}
      width="100%"
      height="100%"
      controls
      playsinline
      config={{
        youtube: {
          playerVars: {
            autoplay: 1,
          },
        },
        file: {
          attributes: {
            autoPlay: true,
            disablePictureInPicture: true,
            controlsList: 'nodownload noremoteplayback',
          },
        },
      }}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
      }}
    />
  </EmbeddedVideoContainer>
);

VideoPlayer.propTypes = {
  url: PropTypes.string.isRequired,
};

const Zoom = ({ url, locale }) => {
  const {
    userSession: {
      name,
      email,
    },
  } = useContext(UserSessionContext);
  const { features } = useSelector((s: any) => s.eventConfigs);
  if (features.includes('hideEmbeddedZoom')) {
    return null;
  }
  return (
    <EmbeddedZoomWrapper id="embedded-video-container">
      <StyledIframe
        id="ZoomFrame"
        src={queryString.stringifyUrl({
          url: ZOOM_HOSTING_ENDPOINT,
          query: {
            link: url,
            userName: name,
            userEmail: email,
            locale,
          },
        })}
        title="Zoom Meeting"
        allow="camera;microphone"
        allowFullScreen
        scrolling="no"
      />
    </EmbeddedZoomWrapper>
  );
};

Zoom.propTypes = {
  url: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
};

const Dacast: FC<{ link: string }> = ({ link }) => (
  <EmbeddedVideoContainer id="embedded-video-container">
    <StyledIframe
      id="DacastStream"
      src={link}
      title="Dast Stream"
      allowFullScreen
      scrolling="no"
    />
  </EmbeddedVideoContainer>
);

const VExpoLiveStream = ({ link }) => {
  const {
    userSession: {
      authToken,
    },
  } = useContext(UserSessionContext);
  return (
    <EmbeddedVideoContainer id="embedded-video-container">
      <StyledIframe
        id="vexpo-live-stream-frame"
        src={queryString.stringifyUrl({
          url: link,
          query: {
            vexpo_auth_token: authToken,
          },
        })}
        title="Vexpo Live Stream"
        allow="camera;microphone"
        allowFullScreen
        scrolling="no"
        sandbox="allow-same-origin allow-scripts"
      />
    </EmbeddedVideoContainer>
  );
};

const WebinarMedia = ({ webinar, isLive }) => {
  const locale = useSelector(selectLocale);
  const { features } = useSelector((s: any) => s.eventConfigs);
  const translation = webinar.translations[locale] || {};
  const browser = detect();
  const streamableContent = useMemo(() => convertStreamableLink(
    // eslint-disable-next-line camelcase
    translation.cloudflare_stream_id
    || translation.url,
  ), [translation.cloudflare_stream_id, translation.url]);
  const webinarTitleForTracking = findAvailableLocale(webinar?.translations || {}, 'en')?.title;
  const vexpoLiveWebinarUrl = useEncodedLiveWebinarUrl(webinarTitleForTracking || '', streamableContent.url);

  switch (streamableContent.type) {
    case 'cfStream': {
      return !!streamableContent.id && (<CloudFareStream id={streamableContent.id} />);
    }
    case 'direct': {
      return !!streamableContent.url && (<VideoPlayer url={streamableContent.url} />);
    }
    case 'zoom': {
      return !!streamableContent.url && (isLive || features.includes('allowWebinarOverrun')) && (browser && !(browser.name === 'ie' || browser.os === 'iOS')) && (
        <Zoom
          url={streamableContent.url}
          locale={locale}
        />
      );
    }
    case 'dacast': {
      return !!streamableContent.url && <Dacast link={streamableContent.url} />;
    }
    case 'vExpoLiveStream': {
      return !!vexpoLiveWebinarUrl && <VExpoLiveStream link={vexpoLiveWebinarUrl} />;
    }
    default: return null;
  }
};

WebinarMedia.propTypes = {
  webinar: WebinarPropType.isRequired,
  isLive: PropTypes.bool.isRequired,
};

export default WebinarMedia;
